import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView, LogBox } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import { scaleOrdinal } from 'd3-scale';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import CustomDT from '../common/controls/customDataTable';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { Loader } from '../../Common/Loader';
import { colorType5, logStyles } from './LogStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import LogKpiRow from './LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import { ISitee } from '../../ClientLanding';
import { useNav } from '../../../providers/NavProvider';

interface Props {
  siteId: string;
  logTitle: string;
  sitesResp: ISitee;
  projectId: string;
}

export default function VibrationMonitoring(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);
  const [vibrationLogData, setVibrationLogData] = useState<null | any>(null);
  const [vibrationLogColumns, setVibrationLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { sitesResp } = useNav();

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const logTitle = props.logTitle;
          const accessToken = await getAccessToken();
          const json = await getAllItems(logTitle, accessToken, clientAPIURL, props.siteId, props.projectId, packageSite.id);
          setVibrationLogData(json.items);
          setVibrationLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return vibrationLogData && vibrationLogColumns
    ? <CreateVibrationLogCharts vibrationLogData={vibrationLogData} vibrationLogColumns={vibrationLogColumns} siteId={props.siteId} logTitle={props.logTitle} clientAPIURL={clientAPIURL} />
    : <Loader />;
}

interface CreateVibrationLogChartsProps {
  vibrationLogData: any;
  vibrationLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
}

const CreateVibrationLogCharts: React.FC<CreateVibrationLogChartsProps> = ({ vibrationLogData, vibrationLogColumns, siteId, logTitle, clientAPIURL }) => {
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';
  const { getAccessToken } = useAuth();

  const ColumnsWithIDColumn = vibrationLogColumns && [
    ...vibrationLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const [filteredData, setFilteredData] = useState<any[]>(vibrationLogData ?? []);

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const newItem = { ...result.items[0] };
        setFilteredData(prev => {
          const without = prev.filter((oItem: any) => oItem.ID !== item.ID);
          return [...without, newItem];
        });
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  const chartData = useMemo(() => {
    const uniquePeriod = [...new Set(filteredData.map((item: any) => item.Period))] as string[];

    const colorLine = scaleOrdinal(schemeCategory10);
    const makeDatasets = (series: number[][]) =>
      series.map((values, index) => ({
        data: values,
        strokeWidth: 2,
        color: (opacity = 1) => colorLine(String(index)) + Math.round(opacity * 255).toString(16),
      }));

    // Conformity / Non Conformity counts per period
    const confPerPeriod = uniquePeriod.map(p => filteredData.filter((i: any) => i.Period === p && i.Conformity === 'Conformity').length);
    const nonConfPerPeriod = uniquePeriod.map(p => filteredData.filter((i: any) => i.Period === p && i.Conformity === 'Non Conformity').length);

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineData1 = { labels: uniquePeriod, datasets: makeDatasets([confPerPeriod, nonConfPerPeriod]) };

    // Max and Average MonitoringResultsMaxPPVmms per period
    const maxByPeriod = uniquePeriod.map(p => {
      const items = filteredData.filter((i: any) => i.Period === p);
      return items.reduce((max: number, i: any) => Math.max(max, i.MonitoringResultsMaxPPVmms), 0);
    });
    const avgByPeriod = uniquePeriod.map(p => {
      const items = filteredData.filter((i: any) => i.Period === p);
      if (items.length === 0) return 0;
      return items.reduce((sum: number, i: any) => sum + i.MonitoringResultsMaxPPVmms, 0) / items.length;
    });
    const lineDataLevelChart = { labels: uniquePeriod, datasets: makeDatasets([maxByPeriod, avgByPeriod]) };

    // PieChartComp contract: data[].name=string (non-null, non-empty), data[].value=number, data[].color=string
    const pieAcc: Record<string, number> = {};
    filteredData.forEach((i: any) => { if (i.Conformity) pieAcc[i.Conformity] = (pieAcc[i.Conformity] ?? 0) + 1; });
    const byConformityPieData = Object.entries(pieAcc).map(([name, value], idx) => ({
      name, value, color: colorType5(idx.toString()),
    }));

    // Tables tab: period summary
    const filteredDataByPeriod = uniquePeriod.map(Period => {
      const filteredItems = filteredData.filter((item: any) => item.Period === Period);
      const TotalConfirmity: any = filteredItems.filter((item: any) => item.Conformity === 'Conformity').length;
      const TotalNonConfirmity: any = filteredItems.filter((item: any) => item.Conformity === 'Non Conformity').length;
      const itemCount = filteredItems.length;
      const sumMonitoringResults = filteredItems.reduce((sum: number, item: any) => sum + item.MonitoringResultsMaxPPVmms, 0);
      const Average = itemCount > 0 ? sumMonitoringResults / itemCount : 0;
      const Maximum = itemCount > 0 ? Math.max(...filteredItems.map((item: any) => item.MonitoringResultsMaxPPVmms)) : 0;
      const Lower = 3;
      const Higher = 10;
      const denom = parseFloat(TotalConfirmity) + parseFloat(TotalNonConfirmity);
      const ConformityPercentage = denom > 0 ? ((parseFloat(TotalConfirmity) / denom) * 100).toFixed(2) : '0.00';
      const NonConformityPercentage = denom > 0 ? ((parseFloat(TotalNonConfirmity) / denom) * 100).toFixed(2) : '0.00';
      return { "Period": Period, "Number Of Results": itemCount, "Average": Average, "Maximum": Maximum, "Lower": Lower, "Higher": Higher, "Confirmity": TotalConfirmity, "Confirmity Percentage": ConformityPercentage, "Non Conformity": TotalNonConfirmity, "Non Confirmity Percentage": NonConformityPercentage };
    });
    const columnKeys = filteredDataByPeriod[0]
      ? Object.keys(filteredDataByPeriod[0]).map((key, index) => ({ ColumnName: key, Order: index }))
      : [];

    return { uniquePeriod, lineData1, lineDataLevelChart, byConformityPieData, filteredDataByPeriod, columnKeys };
  }, [filteredData]);

  const legendData1 = [{ name: 'Conformity' }, { name: 'Non Conformity' }];
  const legendData2 = [{ name: 'Maximum' }, { name: 'Average' }];

  const exceedanceDateSet = new Set(
    filteredData.filter((i: any) => i.Conformity === 'Non Conformity' && i.Date).map((i: any) => i.Date)
  );
  const allDateSet = new Set(filteredData.filter((i: any) => i.Date).map((i: any) => i.Date));
  const exceedanceDays = exceedanceDateSet.size;
  const nonExceedanceDays = [...allDateSet].filter(d => !exceedanceDateSet.has(d)).length;
  const exceedanceEntries = filteredData.filter((i: any) => i.Conformity === 'Non Conformity').length;
  const nonExceedanceEntries = filteredData.filter((i: any) => i.Conformity === 'Conformity').length;

  const kpiItems = [
    { label: 'Total', value: allDateSet.size, unit: 'days', subtitle: `${filteredData.length} entries`, color: '#1565C0' },
    { label: 'Non Exceedances', value: nonExceedanceDays, unit: 'days', subtitle: `${nonExceedanceEntries} entries`, color: '#2E7D32' },
    { label: 'Exceedances', value: exceedanceDays, unit: 'days', subtitle: `${exceedanceEntries} entries`, color: '#C62828' },
  ];

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab && defaultTab === 'list' ? 2 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={{ zIndex: 200 }}>
                <LogKpiRow items={kpiItems}>
                  <LogDateRangeFilter data={vibrationLogData} onFilterChange={setFilteredData} />
                </LogKpiRow>
              </View>
              <View>
                <View style={logStyles.container}>
                  {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.byConformityPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative Data)'} ChartTitle={"Vibration Results"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={legendData1} data={chartData.lineData1} label={chartData.uniquePeriod} subTitle={'(Monthly Data)'} ChartTitle={"Vibration Results"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={legendData2} data={chartData.lineDataLevelChart} label={chartData.uniquePeriod} subTitle={'(Monthly by Average)'} ChartTitle={"Total"} />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
          <TabScreen label="Tables" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    <CustomDT
                      data={chartData.filteredDataByPeriod}
                      columns={chartData.columnKeys}
                      siteId={siteId}
                      logTitle={logTitle}
                      tableId={'VibrationMonitoring'}
                      tableTitle={"Vibration Monitoring"}
                    />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {filteredData && ColumnsWithIDColumn && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                        funcSetShowFilter={() => {}}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
